<template>
  <div v-if="sudoState.active" class="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
    <div class="w-full max-w-sm rounded-2xl bg-white p-6 dark:bg-gray-900">
      <div class="mb-3 flex items-center gap-2">
        <ShieldAlert class="h-5 w-5 text-warning-500" />
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">تأییدِ مجددِ هویت</h3>
      </div>
      <p class="prose-fa mb-4 text-sm text-gray-500 dark:text-gray-400">
        این عملیات حساس است. برای ادامه، رمزِ عبور یا کدِ ورودِ دومرحله‌ایِ خود را وارد کنید.
      </p>

      <form @submit.prevent="submit" class="space-y-3">
        <!-- `.input-field` rather than a hand-rolled copy (§5.0): this pair spelled the SAME six
             properties the global class spells, at `h-11` — 44px beside the 36px buttons below it,
             the exact 4px-family mismatch §3.5 counted on 40 of the panel's 72 mixed-control rows.
             The class is global now, so the field also inherits `main.css`'s AA repaint of
             `border-gray-300` and the RTL <select> chevron rules without restating either. -->
        <input
          v-model="password"
          type="password"
          dir="ltr"
          placeholder="رمزِ عبور"
          autocomplete="current-password"
          class="input-field"
        />
        <!-- `tracking-widest` survives here and only here: §8.3 forbids letter-spacing on Persian
             because it breaks the cursive joins, and preserves it on the OTP/2FA fields — this one
             is named in the spec — where the element is `dir="ltr"` digits entered one character at
             a time. -->
        <input
          v-model="code"
          inputmode="numeric"
          dir="ltr"
          placeholder="یا کدِ دومرحله‌ای (اختیاری)"
          class="input-field text-center tracking-widest"
        />
        <p v-if="sudoState.error" role="alert" class="text-sm text-error-600 dark:text-error-400">{{ sudoState.error }}</p>
        <!-- The confirm button used to read a bare «تأیید» and swap to «در حال تأیید…» while the
             request was in flight. Both halves are banned: R-C2 bans «تأیید» as a whole label (a
             confirm button must restate the action it confirms), and §5.1 records that a button
             whose text changes while loading has lost the only string that said what it was doing.
             One verb phrase naming its object, plus `aria-busy`. -->
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="inline-flex h-9 items-center justify-center rounded-lg border border-gray-300 px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-white/5" @click="cancel">
            انصراف
          </button>
          <button type="submit" :disabled="busy || (!password && !code)" class="inline-flex h-9 items-center justify-center rounded-lg bg-brand-action px-4 text-sm font-medium text-white transition hover:bg-brand-action-hover disabled:cursor-not-allowed disabled:opacity-50" :aria-busy="busy ? 'true' : undefined">
            تأییدِ هویت
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ShieldAlert } from 'lucide-vue-next'
import { sudoState, resolveSudo, cancelSudo } from '@/services/sudo'
import api from '@/services/api'

const password = ref('')
const code = ref('')
const busy = ref(false)

const submit = async () => {
  busy.value = true
  sudoState.error = ''
  try {
    // The /auth/sudo endpoint is exempt from the 401→login bounce, so a wrong password just errors here.
    await api.post('/auth/sudo', { password: password.value || undefined, code: code.value || undefined })
    password.value = ''
    code.value = ''
    resolveSudo()
  } catch (_e) {
    sudoState.error = 'تأییدِ هویت انجام نشد. رمزِ عبور یا کدِ دومرحله‌ای را بررسی و دوباره تلاش کنید.'
  } finally {
    busy.value = false
  }
}

const cancel = () => {
  password.value = ''
  code.value = ''
  cancelSudo()
}
</script>
